/* eslint-disable max-lines */
import type { Span, SpanAttributes } from '@sentry/core';
import {
  BROWSER_NAVIGATION_TIMING_SPAN_NAMES,
  getActiveSpan,
  parseUrl,
  _INTERNAL_performanceTimeToSeconds,
  RESOURCE_SPAN_NAME_FALLBACK,
  setMeasurement,
  spanToJSON,
  filterCollectedUrl,
  UI_LONG_TASK_SPAN_NAME_FALLBACK,
} from '@sentry/core';
import {
  BROWSER_PAINT_TYPE,
  CODE_FILE_PATH,
  CODE_FUNCTION_NAME,
  HTTP_REQUEST_SAME_ORIGIN,
  HTTP_RESPONSE_BODY_SIZE,
  HTTP_RESPONSE_SIZE,
  HTTP_RESPONSE_STATUS_CODE,
  NETWORK_CONNECTION_EFFECTIVE_TYPE,
  NETWORK_CONNECTION_RTT,
  NETWORK_CONNECTION_TYPE,
  SENTRY_OP,
  SERVER_ADDRESS,
  URL_DOMAIN,
  URL_FULL,
  URL_SCHEME,
  SENTRY_ORIGIN,
} from '@sentry/conventions/attributes';
import {
  BROWSER_CACHE,
  BROWSER_CONNECT,
  BROWSER_DNS,
  BROWSER_DOM_CONTENT_LOADED_EVENT,
  BROWSER_LOAD_EVENT,
  BROWSER_PAINT,
  BROWSER_REDIRECT,
  BROWSER_REQUEST,
  BROWSER_RESPONSE,
  BROWSER_TLS_SSL,
  BROWSER_UNLOAD_EVENT,
  RESOURCE_OTHER,
  UI_LONG_ANIMATION_FRAME,
  UI_LONG_TASK,
} from '@sentry/conventions/op';
import {
  addPerformanceInstrumentationHandler,
  type PerformanceLongAnimationFrameTiming,
} from '../instrumentation/performanceObserver';
import { WINDOW } from '../types';
import { resourceTimingToSpanAttributes } from './resourceTiming';
import { getBrowserPerformanceAPI, isMeasurementValue, msToSec, startAndEndSpan } from './utils';

interface NavigatorNetworkInformation {
  readonly connection?: NetworkInformation;
}

// http://wicg.github.io/netinfo/#connection-types
type ConnectionType = 'bluetooth' | 'cellular' | 'ethernet' | 'mixed' | 'none' | 'other' | 'unknown' | 'wifi' | 'wimax';

// http://wicg.github.io/netinfo/#effectiveconnectiontype-enum
type EffectiveConnectionType = '2g' | '3g' | '4g' | 'slow-2g';

// http://wicg.github.io/netinfo/#dom-megabit
type Megabit = number;
// http://wicg.github.io/netinfo/#dom-millisecond
type Millisecond = number;

// http://wicg.github.io/netinfo/#networkinformation-interface
interface NetworkInformation extends EventTarget {
  // http://wicg.github.io/netinfo/#type-attribute
  readonly type?: ConnectionType;
  // http://wicg.github.io/netinfo/#effectivetype-attribute
  readonly effectiveType?: EffectiveConnectionType;
  // http://wicg.github.io/netinfo/#downlinkmax-attribute
  readonly downlinkMax?: Megabit;
  // http://wicg.github.io/netinfo/#downlink-attribute
  readonly downlink?: Megabit;
  // http://wicg.github.io/netinfo/#rtt-attribute
  readonly rtt?: Millisecond;
  // http://wicg.github.io/netinfo/#savedata-attribute
  readonly saveData?: boolean;
  // http://wicg.github.io/netinfo/#handling-changes-to-the-underlying-connection
  onchange?: EventListener;
}

// https://w3c.github.io/device-memory/#sec-device-memory-js-api
interface NavigatorDeviceMemory {
  readonly deviceMemory?: number;
}

const MAX_INT_AS_BYTES = 2147483647;

let _performanceCursor: number = 0;

/**
 * Start tracking long tasks.
 */
export function startTrackingLongTasks(): void {
  addPerformanceInstrumentationHandler('longtask', ({ entries }) => {
    const parent = getActiveSpan();
    if (!parent) {
      return;
    }

    const { attributes: parentAttributes, start_timestamp: parentStartTimestamp } = spanToJSON(parent);

    for (const entry of entries) {
      const startTime = _INTERNAL_performanceTimeToSeconds(entry.startTime);
      if (!startTime) {
        continue;
      }
      const duration = msToSec(entry.duration);

      if (parentAttributes[SENTRY_OP] === 'navigation' && parentStartTimestamp && startTime < parentStartTimestamp) {
        // Skip adding a span if the long task started before the navigation started.
        // `startAndEndSpan` will otherwise adjust the parent's start time to the span's start
        // time, potentially skewing the duration of the actual navigation as reported via our
        // routing instrumentations
        continue;
      }

      startAndEndSpan(parent, startTime, startTime + duration, {
        name: UI_LONG_TASK_SPAN_NAME_FALLBACK,
        op: UI_LONG_TASK,
        attributes: {
          [SENTRY_ORIGIN]: 'auto.ui.browser.metrics',
        },
      });
    }
  });
}

/**
 * Start tracking long animation frames.
 */
export function startTrackingLongAnimationFrames(): void {
  // NOTE: the current web-vitals version (3.5.2) does not support long-animation-frame, so
  // we directly observe `long-animation-frame` events instead of through the web-vitals
  // `observe` helper function.
  const observer = new PerformanceObserver(list => {
    const parent = getActiveSpan();
    if (!parent) {
      return;
    }
    for (const entry of list.getEntries() as PerformanceLongAnimationFrameTiming[]) {
      const startTime = _INTERNAL_performanceTimeToSeconds(entry.startTime);
      if (!startTime || !entry.scripts[0]) {
        continue;
      }

      const {
        start_timestamp: parentStartTimestamp,
        attributes: { [SENTRY_OP]: parentOp },
      } = spanToJSON(parent);

      if (parentOp === 'navigation' && parentStartTimestamp && startTime < parentStartTimestamp) {
        // Skip adding the span if the long animation frame started before the navigation started.
        // `startAndEndSpan` will otherwise adjust the parent's start time to the span's start
        // time, potentially skewing the duration of the actual navigation as reported via our
        // routing instrumentations
        continue;
      }
      const duration = msToSec(entry.duration);

      const attributes: SpanAttributes = {
        [SENTRY_ORIGIN]: 'auto.ui.browser.metrics',
      };

      const initialScript = entry.scripts[0];
      const { invoker, invokerType, sourceURL, sourceFunctionName, sourceCharPosition } = initialScript;
      attributes['browser.script.invoker'] = invoker;
      attributes['browser.script.invoker_type'] = invokerType;
      if (sourceURL) {
        attributes[CODE_FILE_PATH] = sourceURL;
      }
      if (sourceFunctionName) {
        attributes[CODE_FUNCTION_NAME] = sourceFunctionName;
      }
      if (sourceCharPosition !== -1) {
        attributes['browser.script.source_char_position'] = sourceCharPosition;
      }

      startAndEndSpan(parent, startTime, startTime + duration, {
        name: UI_LONG_TASK_SPAN_NAME_FALLBACK,
        op: UI_LONG_ANIMATION_FRAME,
        attributes,
      });
    }
  });

  observer.observe({ type: 'long-animation-frame', buffered: true });
}

interface AddPerformanceEntriesOptions {
  /**
   * Resource spans with `op`s matching strings in the array will not be emitted.
   *
   * Default: []
   */
  ignoreResourceSpans: Array<'resource.script' | 'resource.css' | 'resource.img' | 'resource.other' | string>;

  /**
   * Whether span streaming is enabled.
   */
  spanStreamingEnabled?: boolean;
}

/** Add performance related spans to a transaction */
export function addPerformanceEntries(span: Span, options: AddPerformanceEntriesOptions): void {
  const performance = getBrowserPerformanceAPI();
  if (!performance?.getEntries) {
    // Gatekeeper if performance API not available
    return;
  }

  const { spanStreamingEnabled, ignoreResourceSpans } = options;

  const performanceEntries = performance.getEntries();

  const { attributes, start_timestamp: transactionStartTime } = spanToJSON(span);

  performanceEntries.slice(_performanceCursor).forEach(entry => {
    const startTimestamp = _INTERNAL_performanceTimeToSeconds(entry.startTime);
    if (!startTimestamp) {
      return;
    }
    const duration = msToSec(
      // Inexplicably, Chrome sometimes emits a negative duration. We need to work around this.
      // There is a SO post attempting to explain this, but it leaves one with open questions: https://stackoverflow.com/questions/23191918/peformance-getentries-and-negative-duration-display
      // The way we clamp the value is probably not accurate, since we have observed this happen for things that may take a while to load, like for example the replay worker.
      // TODO: Investigate why this happens and how to properly mitigate. For now, this is a workaround to prevent transactions being dropped due to negative duration spans.
      Math.max(0, entry.duration),
    );

    if (attributes[SENTRY_OP] === 'navigation' && transactionStartTime && startTimestamp < transactionStartTime) {
      return;
    }

    switch (entry.entryType) {
      case 'navigation': {
        _addNavigationSpans(span, entry as PerformanceNavigationTiming, spanStreamingEnabled);
        break;
      }
      case 'paint': {
        _addPaintSpan(span, entry, startTimestamp, duration);
        break;
      }
      case 'resource': {
        _addResourceSpans(
          span,
          entry as PerformanceResourceTiming,
          entry.name,
          startTimestamp,
          duration,
          ignoreResourceSpans,
          spanStreamingEnabled,
        );
        break;
      }
      // Ignore other entry types.
    }
  });

  _performanceCursor = Math.max(performanceEntries.length - 1, 0);

  _trackNavigator(span, spanStreamingEnabled);
}

/**
 * Create a span for a browser paint performance entry.
 * Exported only for tests.
 */
export function _addPaintSpan(span: Span, entry: PerformanceEntry, startTimestamp: number, duration: number): void {
  startAndEndSpan(span, startTimestamp, startTimestamp + duration, {
    // The entry name (`first-paint`, `first-contentful-paint`) is already the low-cardinality name
    // the conventions ask for, so only the attribute backing it has to be added.
    name: entry.name,
    attributes: {
      [SENTRY_OP]: BROWSER_PAINT,
      [SENTRY_ORIGIN]: 'auto.resource.browser.metrics',
      [BROWSER_PAINT_TYPE]: entry.name,
    },
  });
}

/**
 * Instrument navigation entries
 * exported only for tests
 */
export function _addNavigationSpans(
  span: Span,
  entry: PerformanceNavigationTiming,
  spanStreamingEnabled?: boolean,
): void {
  _addPerformanceNavigationTiming(span, entry, 'unloadEvent', spanStreamingEnabled);
  _addPerformanceNavigationTiming(span, entry, 'redirect', spanStreamingEnabled);
  _addPerformanceNavigationTiming(span, entry, 'domContentLoadedEvent', spanStreamingEnabled);
  _addPerformanceNavigationTiming(span, entry, 'loadEvent', spanStreamingEnabled);
  _addPerformanceNavigationTiming(span, entry, 'connect', spanStreamingEnabled);
  _addPerformanceNavigationTiming(span, entry, 'secureConnection', spanStreamingEnabled);
  _addPerformanceNavigationTiming(span, entry, 'fetch', spanStreamingEnabled);
  _addPerformanceNavigationTiming(span, entry, 'domainLookup', spanStreamingEnabled);

  _addRequest(span, entry, spanStreamingEnabled);
}

/**
 * Navigations can happen long after page load, after the time origin was corrected for drift. We use the origin from
 * the entry's start for all its timings, so its duration stays correct.
 */
function _navigationTimeToSeconds(entry: PerformanceNavigationTiming, time: number): number {
  // The cast is safe: `addPerformanceEntries` only adds navigation spans if the time origin is available.
  return _INTERNAL_performanceTimeToSeconds(time, entry.startTime) as number;
}

type StartEventName =
  | 'secureConnection'
  | 'fetch'
  | 'domainLookup'
  | 'unloadEvent'
  | 'redirect'
  | 'connect'
  | 'domContentLoadedEvent'
  | 'loadEvent';

const NAVIGATION_TIMING_SPAN_OPS = {
  secureConnection: BROWSER_TLS_SSL,
  fetch: BROWSER_CACHE,
  domainLookup: BROWSER_DNS,
  unloadEvent: BROWSER_UNLOAD_EVENT,
  redirect: BROWSER_REDIRECT,
  connect: BROWSER_CONNECT,
  domContentLoadedEvent: BROWSER_DOM_CONTENT_LOADED_EVENT,
  loadEvent: BROWSER_LOAD_EVENT,
} as const satisfies Record<StartEventName, string>;

type EndEventName =
  | 'domainLookupStart'
  | 'domainLookupEnd'
  | 'unloadEventEnd'
  | 'redirectEnd'
  | 'connectEnd'
  | 'domContentLoadedEventEnd'
  | 'loadEventEnd';

/** Create performance navigation related spans */
function _addPerformanceNavigationTiming(
  span: Span,
  entry: PerformanceNavigationTiming,
  event: StartEventName,
  spanStreamingEnabled: boolean | undefined,
): void {
  const eventEnd = _getEndPropertyNameForNavigationTiming(event) satisfies keyof PerformanceNavigationTiming;
  const end = entry[eventEnd];
  const start = entry[`${event}Start`];
  if (!start || !end) {
    return;
  }
  const op = NAVIGATION_TIMING_SPAN_OPS[event];
  const filteredUrl = filterCollectedUrl(entry.name);
  startAndEndSpan(span, _navigationTimeToSeconds(entry, start), _navigationTimeToSeconds(entry, end), {
    // With span streaming, span names have to be low cardinality, so we can't fall back to the
    // document URL. `url.full` keeps it, and is what Relay derives the description from.
    name: spanStreamingEnabled ? BROWSER_NAVIGATION_TIMING_SPAN_NAMES[op] : filteredUrl,
    attributes: {
      [SENTRY_OP]: op,
      [SENTRY_ORIGIN]: 'auto.ui.browser.metrics',
      [URL_FULL]: filteredUrl,
      ...(event === 'redirect' && entry.redirectCount != null ? { 'http.redirect_count': entry.redirectCount } : {}),
    },
  });
}

function _getEndPropertyNameForNavigationTiming(event: StartEventName): EndEventName {
  if (event === 'secureConnection') {
    return 'connectEnd';
  }
  if (event === 'fetch') {
    return 'domainLookupStart';
  }
  return `${event}End`;
}

/** Create request and response related spans */
function _addRequest(span: Span, entry: PerformanceNavigationTiming, spanStreamingEnabled: boolean | undefined): void {
  const requestStartTimestamp = _navigationTimeToSeconds(entry, entry.requestStart);
  const responseEndTimestamp = _navigationTimeToSeconds(entry, entry.responseEnd);
  const responseStartTimestamp = _navigationTimeToSeconds(entry, entry.responseStart);
  if (entry.responseEnd) {
    // It is possible that we are collecting these metrics when the page hasn't finished loading yet, for example when the HTML slowly streams in.
    // In this case, ie. when the document request hasn't finished yet, `entry.responseEnd` will be 0.
    // In order not to produce faulty spans, where the end timestamp is before the start timestamp, we will only collect
    // these spans when the responseEnd value is available. The backend (Relay) would drop the entire span if it contained faulty spans.

    // With span streaming, span names have to be low cardinality, so we can't fall back to the
    // document URL. `url.full` keeps it, and is what Relay derives the description from.
    const filteredUrl = filterCollectedUrl(entry.name);

    startAndEndSpan(span, requestStartTimestamp, responseEndTimestamp, {
      name: spanStreamingEnabled ? BROWSER_NAVIGATION_TIMING_SPAN_NAMES[BROWSER_REQUEST] : filteredUrl,
      attributes: {
        [SENTRY_OP]: BROWSER_REQUEST,
        [SENTRY_ORIGIN]: 'auto.ui.browser.metrics',
        [URL_FULL]: filteredUrl,
      },
    });

    startAndEndSpan(span, responseStartTimestamp, responseEndTimestamp, {
      name: spanStreamingEnabled ? BROWSER_NAVIGATION_TIMING_SPAN_NAMES[BROWSER_RESPONSE] : filteredUrl,
      attributes: {
        [SENTRY_OP]: BROWSER_RESPONSE,
        [SENTRY_ORIGIN]: 'auto.ui.browser.metrics',
        [URL_FULL]: filteredUrl,
      },
    });
  }
}

/**
 * Create resource-related spans.
 * Exported only for tests.
 */
export function _addResourceSpans(
  span: Span,
  entry: PerformanceResourceTiming,
  resourceUrl: string,
  startTimestamp: number,
  duration: number,
  ignoredResourceSpanOps?: Array<string>,
  spanStreamingEnabled?: boolean,
): void {
  // we already instrument based on fetch and xhr, so we don't need to
  // duplicate spans here.
  if (entry.initiatorType === 'xmlhttprequest' || entry.initiatorType === 'fetch') {
    return;
  }

  const op = entry.initiatorType ? `resource.${entry.initiatorType}` : RESOURCE_OTHER;
  if (ignoredResourceSpanOps?.includes(op)) {
    return;
  }

  const attributes: SpanAttributes = {
    [SENTRY_ORIGIN]: 'auto.resource.browser.metrics',
  };

  const parsedUrl = parseUrl(resourceUrl);

  if (parsedUrl.protocol) {
    attributes[URL_SCHEME] = parsedUrl.protocol.split(':').pop(); // the protocol returned by parseUrl includes a :, but OTEL spec does not, so we remove it.
  }

  // `host` is the URL authority, so it can carry userinfo, which doesn't belong on either attribute.
  const host = parsedUrl.host?.replace(/^.*@/, '');

  if (host) {
    attributes[SERVER_ADDRESS] = host;
  }

  // Unlike `server.address`, `url.domain` excludes the port.
  const domain = host?.replace(/:\d+$/, '');

  if (domain) {
    attributes[URL_DOMAIN] = domain;
  }

  attributes[HTTP_REQUEST_SAME_ORIGIN] = resourceUrl.includes(WINDOW.location.origin);

  const filteredUrl = filterCollectedUrl(resourceUrl);
  attributes[URL_FULL] = filteredUrl;

  _setResourceRequestAttributes(entry, attributes, [
    // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming/responseStatus
    ['responseStatus', HTTP_RESPONSE_STATUS_CODE],

    ['transferSize', HTTP_RESPONSE_SIZE],
    ['encodedBodySize', HTTP_RESPONSE_BODY_SIZE],
    ['decodedBodySize', 'http.response.body.decoded_size'],

    // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming/renderBlockingStatus
    ['renderBlockingStatus', 'resource.render_blocking_status'],

    // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming/deliveryType
    ['deliveryType', 'http.response_delivery_type'],
  ]);

  const attributesWithResourceTiming: SpanAttributes = { ...attributes, ...resourceTimingToSpanAttributes(entry) };

  const endTimestamp = startTimestamp + duration;

  startAndEndSpan(span, startTimestamp, endTimestamp, {
    // With span streaming, span names have to be low cardinality, so we can't fall back to the URL.
    name: spanStreamingEnabled
      ? domain || RESOURCE_SPAN_NAME_FALLBACK
      : filteredUrl.replace(WINDOW.location.origin, ''),
    op,
    attributes: attributesWithResourceTiming,
  });
}

/**
 * Capture the information of the user agent.
 * TODO(v12): Remove non-span-streaming attributes and measurements once the static trace lifecycle is removed
 */
function _trackNavigator(span: Span, spanStreamingEnabled: boolean | undefined): void {
  const navigator = WINDOW.navigator as null | (Navigator & NavigatorNetworkInformation & NavigatorDeviceMemory);
  if (!navigator) {
    return;
  }

  // track network connectivity
  const connection = navigator.connection;
  if (connection) {
    if (connection.effectiveType) {
      span.setAttribute(
        spanStreamingEnabled ? NETWORK_CONNECTION_EFFECTIVE_TYPE : 'effectiveConnectionType',
        connection.effectiveType,
      );
    }

    if (connection.type) {
      span.setAttribute(spanStreamingEnabled ? NETWORK_CONNECTION_TYPE : 'connectionType', connection.type);
    }

    if (isMeasurementValue(connection.rtt)) {
      if (spanStreamingEnabled) {
        span.setAttribute(NETWORK_CONNECTION_RTT, connection.rtt);
      } else if (spanToJSON(span).attributes[SENTRY_OP] === 'pageload') {
        // Measurements are only recorded on the pageload span, matching the historical
        // behavior where `connection.rtt` was only flushed for pageload transactions.
        setMeasurement('connection.rtt', connection.rtt, 'millisecond');
      }
    }
  }

  if (isMeasurementValue(navigator.deviceMemory)) {
    if (spanStreamingEnabled) {
      span.setAttribute('device.memory.estimated_capacity', navigator.deviceMemory);
    } else {
      span.setAttribute('deviceMemory', `${navigator.deviceMemory} GB`);
    }
  }

  if (isMeasurementValue(navigator.hardwareConcurrency)) {
    if (spanStreamingEnabled) {
      span.setAttribute('device.processor_count', navigator.hardwareConcurrency);
    } else {
      span.setAttribute('hardwareConcurrency', String(navigator.hardwareConcurrency));
    }
  }
}

type ExperimentalResourceTimingProperty =
  | 'renderBlockingStatus'
  | 'deliveryType'
  // For some reason, TS during build, errors on `responseStatus` not being a property of
  // PerformanceResourceTiming while it actually is. Hence, we're adding it here.
  // Perhaps because response status is not yet available in Webkit/Safari.
  // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming/responseStatus
  | 'responseStatus';

/**
 * Use this to set any attributes we can take directly form the PerformanceResourceTiming entry.
 *
 * This is just a mapping function for entry->attribute to keep bundle-size minimal.
 * Experimental properties are also accepted (see {@link ExperimentalResourceTimingProperty}).
 * Assumes that all entry properties might be undefined for browser-specific differences.
 * Only accepts string and number values for now and also sets 0-values.
 */
export function _setResourceRequestAttributes(
  entry: Partial<PerformanceResourceTiming> & Partial<Record<ExperimentalResourceTimingProperty, number | string>>,
  attributes: SpanAttributes,
  properties: [keyof PerformanceResourceTiming | ExperimentalResourceTimingProperty, string][],
): void {
  properties.forEach(([entryKey, attributeKey]) => {
    const entryVal = entry[entryKey];
    if (
      entryVal != null &&
      ((typeof entryVal === 'number' && entryVal < MAX_INT_AS_BYTES) || typeof entryVal === 'string')
    ) {
      attributes[attributeKey] = entryVal;
    }
  });
}
